// useBroadcastPreview (issue #449) — fetches the resolved recipient count
// from `GET /chats/jobs/:jobId/broadcasts/preview` before the composer
// commits the broadcast. Replaces the client-side `previewRecipientCount`
// stop-gap from #422 / #447 with the server-authoritative count, so the
// confirmation modal never disagrees with what the server actually
// delivers to.
//
// The hook gates itself on `enabled`: callers pass `enabled: false` until
// the target is chosen, which avoids a wasted request on every keystroke
// in the title/body fields. The composer typically wires this to
// `target != null && step === 'confirm'`.

import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { previewBroadcast } from './api';
import { chatKeys } from './keys';

import type { BroadcastTarget } from 'src/models/chat';

export interface UseBroadcastPreviewArgs {
  jobId: number;
  target: BroadcastTarget;
  customUserIds?: number[];
  /** Skip the request until the caller flips this. Defaults to true. */
  enabled?: boolean;
}

export function useBroadcastPreview(
  { jobId, target, customUserIds, enabled = true }: UseBroadcastPreviewArgs,
  options?: UseQueryOptions<{ recipientCount: number }>
) {
  return useQuery<{ recipientCount: number }>(
    chatKeys.broadcastPreview(jobId, target, customUserIds),
    () => previewBroadcast(jobId, { target, customUserIds }),
    {
      enabled: enabled && Number.isFinite(jobId) && jobId > 0 && !!target,
      // The roster doesn't move between the composer opening and the user
      // confirming — a stale window of a minute is plenty and keeps the
      // confirmation step responsive when the user back-and-forths between
      // targets. The send mutation invalidates the key on success.
      staleTime: 60_000,
      ...options,
    }
  );
}
